import { z } from 'zod';

// Zod schemas for the Authentication module. Password rules mirror the backend
// FluentValidation policy (8–128 chars, upper + lower + digit) used by reset/register.

// Login is Email + Password only. The tenant/company is resolved server-side from the
// authenticated identity (email-based tenant discovery) — never entered by the user.
export const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});
export type LoginFormValues = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({
  tenantCode: z.string().trim().optional().or(z.literal('')),
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
});
export type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

const strongPassword = z
  .string()
  .min(8, 'At least 8 characters')
  .max(128, 'At most 128 characters')
  .regex(/[a-z]/, 'Include a lowercase letter')
  .regex(/[A-Z]/, 'Include an uppercase letter')
  .regex(/\d/, 'Include a digit');

// Reset Password form. The reset token + tenant arrive in the URL (never entered by the user),
// so the form only collects the new password + confirmation.
export const resetPasswordSchema = z
  .object({
    newPassword: strongPassword,
    confirm: z.string().min(1, 'Confirm your password'),
  })
  .refine((d) => d.newPassword === d.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  });
export type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;
